import React from 'react';
import * as yup from 'yup';

import { StyleProps } from '$/components/ui';
import {
  AttendeeField,
  AttendeeFragment,
  InteractionDetails,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionType,
  PublicEWebinarFragment,
  ReactionFragment,
  RegistrationFieldType,
} from '$/GraphQL/schema/types';
import { ContactRequestIcon } from '$/images/FontAwesomeIcons';
import { RenderColumnData } from '$/sections/Home/Dashboard/Registrants/types';
import { TableCellProps } from 'react-virtualized/dist/es/Table';
import { AnalyticsCardProps } from '../cards/types';
import CardMain from '../cards/CardMain';
import {
  AttendeeCardDefaultValues,
  InteractionInfo,
  InteractionModalFooterProps,
  interactionTypeName,
} from '../InteractionInfo';
import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '../types';
import { ContactRequestCard } from './ContactRequestCard';
import { t } from '@lingui/macro';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import ContactRequestModalSettings from './ContactRequestModalSettings';

const ContactRequestModal = React.lazy(() => import('./ContactRequestModal'));
const AnalyticsClickCard = React.lazy(() => import('../cards/AnalyticsClickCard'));
const FormatPhone = React.lazy(() => import('$/utils/phone'));

export class ContactRequestInfo extends InteractionInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public detailsDefault(_interactions: InteractionInfo[]): InteractionDetails {
    return {
      ...super.detailsDefault(_interactions),
      requireEmail: true,
    };
  }

  public typeName(): string | undefined {
    return interactionTypeName(InteractionType.RequestToContact);
  }

  public title(): string {
    return this.i.details?.title ?? 'May we reach out to you?';
  }

  public userCanAdd(): boolean {
    return true;
  }

  public category(): 'Standard' | 'Special' | 'Anchor' | 'Conversion' {
    return 'Standard';
  }

  public hasExpiresInField(): boolean {
    return false;
  }

  public hasShowTimerToggle(): boolean {
    return false;
  }

  public onlyOneInstance(): boolean {
    return false;
  }

  public hasResultsAppearAtField(): boolean {
    return false;
  }

  public hasPropertyNameField(): boolean {
    return true;
  }

  public hasPinToTopToggle(): boolean {
    return true;
  }

  public hasEndsAt(): boolean {
    return false;
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    return yup.object().shape({
      details: yup.object().shape({
        name: yup.string().trim().required(),
        email:
          (this.attendee?.id && !this.attendee.email) || this.details?.requireEmail
            ? yup
                .string()
                .trim()
                .required()
                .email(t`This must be a valid email address`)
            : yup
                .string()
                .trim()
                .email(t`This must be a valid email address`),
        phone: yup
          .string()
          .trim()
          .when('email', {
            is: (email?: string) => !Boolean(email),
            then: (schema) =>
              schema.required(t`Please enter a phone number or email we can reach you at.`),
          }),

        message: yup.string().trim(),
      }),
    });
  }

  public attendeeCardDefaultValues(attendee?: AttendeeFragment): AttendeeCardDefaultValues {
    const phoneField: AttendeeField | undefined =
      attendee &&
      attendee.attendeeFields &&
      Object.values(attendee.attendeeFields as AttendeeField[]).filter(
        (value) => value.type === RegistrationFieldType.Phone
      )[0];
    const phone = phoneField?.value || '';
    return {
      details: {
        name:
          (attendee?.firstName || attendee?.lastName) && attendee?.firstName != 'Attendee'
            ? [attendee?.firstName, attendee?.lastName].join(' ')
            : undefined,
        email: attendee?.email ?? undefined,
        phone,
      },
    };
  }

  public icon(): React.FC<StyleProps> {
    return ContactRequestIcon;
  }

  public isEditable(): boolean {
    return true;
  }

  public detailsKey(): string | undefined {
    return 'requestToContact';
  }

  // Does the interaction generate Answers
  // (Click doesn't count as its simply a reaction not an answer)
  public hasAnswers(): boolean {
    return true;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      ...super.modalValidationRules(),
      email: yup.string().trim().email().required(),
    };
  }

  // Where does primary answer live?
  public answerMessage(
    reaction: Pick<ReactionFragment, 'stringAnswer' | 'numberAnswer' | 'detailsFields'>
  ): string {
    return reaction.detailsFields?.requestToContact?.message ?? '';
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return ContactRequestModal;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook | null {
    return ContactRequestModalSettings;
  }

  public modalFooterComponent(): React.FC<InteractionModalFooterProps> | null {
    return null;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsClickCard;
  }

  public cardComponent(): React.FC<{ onClickHeader?: () => void }> {
    return CardMain.StandardCard;
  }

  public cardBodyComponent(): React.FC {
    return ContactRequestCard;
  }

  // Wants to show results (e.g. at the end of the video)
  public hasResultsToShow(): boolean {
    return false;
  }

  public onlyExistsInPresentationRoom(): boolean {
    return false;
  }

  public renderColumnData(data: RenderColumnData): RenderColumnData | null {
    return {
      ...data,
      flexGrow: 1,
      width: 360,
      cellRenderer: ({ cellData }: TableCellProps): React.ReactNode => {
        const reaction = cellData as ReactionFragment;
        if (!reaction || !reaction.detailsFields) return <div />;
        const phone = reaction.detailsFields?.requestToContact?.phone;

        const name = reaction.detailsFields?.requestToContact?.name;
        const email = reaction.detailsFields?.requestToContact?.email;
        const message = reaction.detailsFields?.requestToContact?.message;
        return (
          <div
            className='leading-normal'
            css={css`
              > div {
                ${tw`flex flex-row`}
              }
              .field {
                ${tw`text-gray-2`} width: 4rem;
              }
              .value {
                ${tw`flex flex-1`}
              }
            `}
          >
            {name && (
              <div>
                <div className='field'>Name:</div>
                <div className='value'>{name}</div>
              </div>
            )}
            {email && (
              <div>
                <div className='field'>Email:</div>
                <div className='value'>
                  <a href={`mailto: ${email}`}>{email}</a>
                </div>
              </div>
            )}
            {phone && (
              <div>
                <div className='field'>Phone:</div>
                <div className='value'>
                  <FormatPhone phone={phone} />
                </div>
              </div>
            )}
            {message && (
              <div>
                <div className='field'>Message:</div>
                <div className='value'>{message}</div>
              </div>
            )}
            {!message && !email && !phone && <div>No information provided</div>}
          </div>
        );
      },
    };
  }

  public messageCardBody(
    reaction: Pick<
      ReactionFragment,
      'stringAnswer' | 'numberAnswer' | 'detailsFields' | 'arrayAnswer'
    >
  ): JSX.Element | null {
    const requestToContact = reaction.detailsFields?.requestToContact;
    return (
      <div>
        <span>
          {requestToContact?.name && <p className='mb-0 leading-normal'>{requestToContact.name}</p>}
          {requestToContact?.email && (
            <div>
              <a className='text-white' href={`mailto:${requestToContact.email}`}>
                {requestToContact?.email}
              </a>
            </div>
          )}
          {requestToContact?.phone && (
            <p>
              <FormatPhone phone={requestToContact.phone} />
            </p>
          )}
        </span>
        {requestToContact?.message && (
          <>
            <div className='mt-1'>{requestToContact.message}</div>
          </>
        )}
      </div>
    );
  }
}
